/**
 * Гостевой вход (бэк #464) — обёртки эндпойнтов guest_access.
 *
 * Персональные роуты (`/v1/guest-access**`) гейтятся на бэке RouteRoleRule
 * (owner/manager/front_desk, право guest_access.create / .approve). Анонимный
 * `/v1/guest-auth/self` JWT не требует — тенант резолвится по поддомену
 * (заголовок X-Tenant-Slug, который ставит customFetch).
 *
 * baseURL зафиксирован на `/v1` — совпадает с контрактом бэка (nginx также
 * проксирует /api/v1 → /v1, но здесь явный /v1, как в auth/platform-me).
 */
import { customFetch } from './openapi-fetch.js';
import type {
  ApproveGuestAccessInput,
  CreateGuestAccessInput,
  CreateGuestAccessResponse,
  GuestAccessGrant,
  GuestAccessListPageResponse,
  GuestAccessListQuery,
  GuestAccessListResponse,
  GuestSelfAuthInput,
  GuestSelfAuthResult,
  GuestSessionResponse,
  GuestTgWebAppResult,
} from '@hsc/types';

const BASE = '/v1';

/** POST /v1/guest-access — персонал выдаёт гостю доступ (201). */
export async function createGuestAccess(
  input: CreateGuestAccessInput,
): Promise<CreateGuestAccessResponse> {
  return customFetch<CreateGuestAccessResponse>({
    url: '/guest-access',
    method: 'POST',
    data: input,
    baseURL: BASE,
  });
}

/**
 * GET /v1/guest-access?status=&page=&page_size= — список грантов тенанта с
 * серверной пагинацией и фильтром-бакетом по статусу (экран «Список доступов»).
 * Без аргументов вернёт первую страницу всех грантов (обратная совместимость:
 * поле `grants` есть всегда).
 */
export async function listGuestAccess(
  query: GuestAccessListQuery = {},
): Promise<GuestAccessListPageResponse> {
  const params: Record<string, string | number | undefined> = {};
  if (query.status) params.status = query.status;
  if (query.page) params.page = query.page;
  if (query.page_size) params.page_size = query.page_size;
  return customFetch<GuestAccessListPageResponse>({
    url: '/guest-access',
    method: 'GET',
    params,
    baseURL: BASE,
  });
}

/** GET /v1/guest-access/pending — очередь на подтверждение. */
export async function listPendingGuestAccess(): Promise<GuestAccessListResponse> {
  return customFetch<GuestAccessListResponse>({
    url: '/guest-access/pending',
    method: 'GET',
    baseURL: BASE,
  });
}

/** POST /v1/guest-access/{id}/approve — подтвердить (с коррекцией дат/номера). */
export async function approveGuestAccess(
  id: string,
  input: ApproveGuestAccessInput = {},
): Promise<GuestAccessGrant> {
  return customFetch<GuestAccessGrant>({
    url: `/guest-access/${encodeURIComponent(id)}/approve`,
    method: 'POST',
    data: input,
    baseURL: BASE,
  });
}

/** POST /v1/guest-access/{id}/reject — отклонить заявку. */
export async function rejectGuestAccess(id: string): Promise<GuestAccessGrant> {
  return customFetch<GuestAccessGrant>({
    url: `/guest-access/${encodeURIComponent(id)}/reject`,
    method: 'POST',
    data: {},
    baseURL: BASE,
  });
}

/** POST /v1/guest-access/{id}/revoke — отозвать выданный доступ. */
export async function revokeGuestAccess(id: string): Promise<GuestAccessGrant> {
  return customFetch<GuestAccessGrant>({
    url: `/guest-access/${encodeURIComponent(id)}/revoke`,
    method: 'POST',
    data: {},
    baseURL: BASE,
  });
}

/**
 * POST /v1/guest-access/{id}/revoke — отменить выданный доступ.
 *
 * Семантический алиас revokeGuestAccess для экрана «Список доступов»: бэк
 * переводит грант в статус `expired` (показывается как «Просрочен») и сразу
 * рубит живую сессию гостя. Тот же эндпойнт revoke.
 */
export async function cancelGuestAccess(id: string): Promise<GuestAccessGrant> {
  return revokeGuestAccess(id);
}

/**
 * POST /v1/guest-auth/self — само-авторизация гостя (анон, tenant по поддомену).
 *
 * Бэк отдаёт 200 с сессией при совпадении и 202 при отсутствии (заявка в
 * очередь). customFetch не различает 200/202 (оба `res.ok`), поэтому
 * дискриминируем по полю `status` в теле:
 *   - "authorized"        → есть токены (вошли как гость);
 *   - "pending_approval"  → нет токенов (ждём подтверждения менеджера).
 *
 * noAuth: true — Authorization не отправляем, даже если в хранилище остался
 * чужой токен (вход на странице логина до авторизации).
 */
export async function guestSelfAuth(
  input: GuestSelfAuthInput,
): Promise<GuestSelfAuthResult> {
  const body = await customFetch<Record<string, unknown>>({
    url: '/guest-auth/self',
    method: 'POST',
    data: input,
    baseURL: BASE,
    noAuth: true,
  });
  if (body && body.status === 'pending_approval') {
    return {
      outcome: 'pending',
      status: 'pending_approval',
      grant_id: String(body.grant_id ?? ''),
      ...(body.access_token ? { access_token: String(body.access_token) } : {}),
      ...(body.refresh_token ? { refresh_token: String(body.refresh_token) } : {}),
    };
  }
  return {
    outcome: 'authorized',
    ...(body as unknown as Omit<
      Extract<GuestSelfAuthResult, { outcome: 'authorized' }>,
      'outcome'
    >),
  };
}

/**
 * POST /v1/guest-auth/tg-webapp — авто-логин Telegram Mini App.
 *
 * Тело: `{ init_data }` — сырой `window.Telegram.WebApp.initData`. Подпись
 * проверяется на бэке токеном бота ИМЕННО этого тенанта (per-tenant), tenant
 * резолвится по поддомену. noAuth: true — не шлём чужой Authorization.
 *
 * Исходы:
 *   - 200 authorized → токены гостя (привязанный TG-юзер);
 *   - 404 guest_access.tg_not_bound → `not_bound` (фронт уводит на self-auth);
 *   - прочие ошибки (401 bad signature, 503) пробрасываются как исключение.
 */
export async function guestTgWebApp(
  initData: string,
): Promise<GuestTgWebAppResult> {
  try {
    const body = await customFetch<GuestSessionResponse>({
      url: '/guest-auth/tg-webapp',
      method: 'POST',
      data: { init_data: initData },
      baseURL: BASE,
      noAuth: true,
    });
    return { outcome: 'authorized', ...body };
  } catch (err) {
    const e = err as { status?: number; body?: { title?: string } };
    if (e.status === 404 && e.body?.title === 'guest_access.tg_not_bound') {
      return { outcome: 'not_bound' };
    }
    throw err;
  }
}
